// Inbox › Tasks (BL-004): the office's tasks and follow-ups. My work by default: your tasks, grouped by
// when they're due (overdue first), with the jobs waiting for your review and the agents' requests
// waiting for your say, each opening where it's handled; then nobody's tasks yet, to pick up;
// everyone's; and the ones closed lately.

import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { ApprovalList, type ApprovalListProps, type Host } from '@flowology/ui-web';
import '@flowology/ui-web/base.css';
import { client } from '../api.ts';
import { AGENTS_PERMS, Header, REVIEW_PERMS, type NavProps } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { useQuery } from '../books/ui.tsx';
import { openJob } from '../job/job-page.ts';
import { TaskList, type TaskQuery } from './TaskList.tsx';
import { queueChip, type QueueJob } from '../review/review-model.ts';
import { approvalLine, type WaitingApproval } from './tasks-model.ts';
import '../books/books.css';
import '../blueprint/inbox.css';
import { useDesign } from '../blueprint/design.ts';

type Part = 'mine' | 'nobody' | 'all' | 'closed';
const PARTS: { part: Part; label: string; query: TaskQuery; empty: string }[] = [
  {
    part: 'mine',
    label: 'My work',
    query: { assignee: 'me' },
    empty: 'Nothing waits on you. Tasks given to you show here.',
  },
  {
    part: 'nobody',
    label: 'Nobody’s yet',
    query: { assignee: 'unassigned' },
    empty: 'Every open task has someone.',
  },
  { part: 'all', label: 'Everyone’s', query: {}, empty: 'No open tasks.' },
  { part: 'closed', label: 'Closed', query: { state: 'closed' }, empty: 'No task has been closed yet.' },
];

export function TasksScreen({
  nav,
  onChanged,
  onOpenReview,
  onOpenApprovals,
}: {
  nav: NavProps;
  onChanged?: () => void;
  /** Review, on this job (My work's jobs waiting for review). */
  onOpenReview?: (jobId: string) => void;
  /** Agents › Requests (My work's requests waiting for a say). */
  onOpenApprovals?: () => void;
}) {
  const timeZone = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const bp = useDesign() === 'blueprint';
  const held = nav.permissions ?? [];
  const reviews = holdsAny(held, REVIEW_PERMS);
  const decides = holdsAny(held, AGENTS_PERMS);
  // My work is everyone's first place: their tasks (with a staff record), review and requests (by permission)
  const parts = nav.staffId || reviews || decides ? PARTS : PARTS.filter((p) => p.part !== 'mine');
  const [part, setPart] = useState<Part>(parts[0]!.part);
  const shown = parts.find((p) => p.part === part)!;
  // My work's parts: your tasks, the jobs waiting for review, the agents' requests waiting for your say
  const myWork = (
    <>
      {nav.staffId && (
        <section className="my-work-part" aria-labelledby="my-tasks-title">
          <h3 id="my-tasks-title">Your tasks</h3>
          <TaskList
            title="Your tasks"
            query={shown.query}
            timeZone={timeZone}
            canAdd
            groupByDue
            empty={shown.empty}
            defaultAssignee={nav.staffId}
            onOpenJob={openJob}
            {...(onChanged ? { onChanged } : {})}
          />
        </section>
      )}
      {reviews && <ReviewWaiting {...(onOpenReview ? { onOpen: onOpenReview } : {})} />}
      {decides && <RequestsWaiting {...(onOpenApprovals ? { onOpen: onOpenApprovals } : {})} />}
    </>
  );
  return (
    <div className="app books tasks-place">
      <Header
        nav={nav}
        title="Tasks"
        lede="Follow-ups and callbacks: what needs doing, by when, and who has it."
      />
      <div className={`books-body${bp ? ' bp-ib' : ''}`}>
        <nav className="books-parts" aria-label="Tasks">
          {parts.map((p) => (
            <button
              key={p.part}
              className={`btn quiet${p.part === part ? ' on' : ''}`}
              aria-current={p.part === part ? 'page' : undefined}
              onClick={() => setPart(p.part)}
            >
              {p.label}
            </button>
          ))}
        </nav>
        <main className="books-main">
          <div className="books-page">
            {part === 'mine' && bp ? (
              // Blueprint: no frame round My work, so its parts are the sections (your tasks on the
              // left, what waits for review and for your say beside them); the tab already names it
              <div className="my-work" role="group" aria-label="My work">
                <h2 className="ib-vh">My work</h2>
                {myWork}
              </div>
            ) : part === 'mine' ? (
              <section className="sheet panel my-work" aria-label="My work">
                <div className="panel-head">
                  <h2>My work</h2>
                </div>
                {myWork}
              </section>
            ) : (
              <section className="sheet panel" aria-label={`Tasks: ${shown.label}`}>
                <div className="panel-head">
                  <h2>{shown.label}</h2>
                </div>
                <TaskList
                  key={part}
                  title={shown.label}
                  query={shown.query}
                  timeZone={timeZone}
                  canAdd={part !== 'closed'}
                  groupByDue={part !== 'closed'}
                  empty={shown.empty}
                  onOpenJob={openJob}
                  {...(onChanged ? { onChanged } : {})}
                />
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

/** My work: the jobs done and waiting for the office's review (jobs.reviewQueue), read only. */
function ReviewWaiting({ onOpen }: { onOpen?: (jobId: string) => void }) {
  const q = useQuery<{ jobs: QueueJob[]; total: number }>('jobs.reviewQueue', { limit: SHOWN });
  const jobs = q.data?.jobs ?? [];
  return (
    <section className="my-work-part" aria-labelledby="my-review-title">
      {/* how many wait in all (the server's count), though only the oldest few are listed */}
      <h3 id="my-review-title">Jobs to review {q.data && <span className="muted">{q.data.total}</span>}</h3>
      {!q.data ? (
        <p className="muted small">{q.error ?? 'Getting the jobs to review…'}</p>
      ) : !jobs.length ? (
        <p className="muted small">No job waits for review.</p>
      ) : (
        <ul className="my-work-list">
          {jobs.map((j) => (
            <li key={j.jobId}>
              <button className="cell-link" onClick={() => onOpen?.(j.jobId)} disabled={!onOpen}>
                Job #{j.number} · {j.customerName}
              </button>
              {/* as Review's own card says it: ready, or the first thing to fix */}
              <span className={`small ${queueChip(j).tone === 'warn' ? 'warn-ink' : 'muted'}`}>
                {queueChip(j).text}
              </span>
            </li>
          ))}
        </ul>
      )}
      {q.data && <MoreThere shown={jobs.length} total={q.data.total} where="Review" />}
    </section>
  );
}

/** My work lists the oldest few jobs; the rest wait in Review. */
const SHOWN = 10;

/** "And 14 more in Review": what My work's short list leaves out, counted. */
function MoreThere({ shown, total, where }: { shown: number; total: number; where: string }) {
  return total > shown ? (
    <p className="muted small">
      And {total - shown} more in {where}.
    </p>
  ) : null;
}

/** ui-web's views in the office: they ask the server as the person, and open places here. */
function officeHost(onApprovals?: () => void): Host {
  return {
    surface: 'office',
    theme: matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    callTool: (name, args) =>
      client.call(name, args).then(
        (result) => ({ ok: true, result }),
        (e: unknown) => ({
          ok: false,
          code: e instanceof RpcError ? e.code : 'internal',
          message: e instanceof Error ? e.message : 'Something went wrong.',
        }),
      ),
    open: (t) => {
      if (t.kind === 'approvals') onApprovals?.();
    },
  };
}

/** My work: the agents' requests waiting for this person's say (approvals.list), read only. */
function RequestsWaiting({ onOpen }: { onOpen?: () => void }) {
  const bp = useDesign() === 'blueprint';
  // approvals.list's whole answer, so Blueprint can hand it to ApprovalList as its props
  // only those this person may decide, chosen and counted by the server (a page filtered here missed some)
  const q = useQuery<{
    approvals: (WaitingApproval & ApprovalListProps['approvals'][number])[];
    total: number;
  }>('approvals.list', { status: 'pending', decidable: true, limit: 20 });
  const mine = q.data?.approvals ?? [];
  const rest = q.data && <MoreThere shown={mine.length} total={q.data.total} where="Approvals" />;
  // Blueprint draws them with ui-web's ApprovalList, the view Home and an AI tool's chat draw too
  if (bp && q.data)
    return (
      <section className="my-work-part" aria-labelledby="my-requests-title">
        <h3 id="my-requests-title">Agent requests</h3>
        {mine.length ? (
          <div className="flw flw-office">
            <ApprovalList data={{ approvals: mine }} host={officeHost(onOpen)} />
          </div>
        ) : (
          <p className="muted small">No agent request waits for your say.</p>
        )}
        {rest}
      </section>
    );
  return (
    <section className="my-work-part" aria-labelledby="my-requests-title">
      <h3 id="my-requests-title">Agent requests {q.data && <span className="muted">{q.data.total}</span>}</h3>
      {!q.data ? (
        <p className="muted small">{q.error ?? 'Getting the agents’ requests…'}</p>
      ) : !mine.length ? (
        <p className="muted small">No agent request waits for your say.</p>
      ) : (
        <ul className="my-work-list">
          {mine.map((a) => (
            <li key={a.id}>
              <button className="cell-link" onClick={() => onOpen?.()} disabled={!onOpen}>
                {approvalLine(a)}
              </button>
              {a.explanation && <span className="muted small">{a.explanation}</span>}
            </li>
          ))}
        </ul>
      )}
      {rest}
    </section>
  );
}
